import type { SkillGraphData, SkillGraphEdge, SkillGraphNode } from "./graph-types";

export interface LayoutNode extends SkillGraphNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

const CLUSTER_PALETTE = [
  "#0d3d32",
  "#a34b00",
  "#2a5080",
  "#523a78",
  "#9c2f2f",
  "#1f5c3f",
  "#6b4a20",
  "#2c4568",
];

export function clusterColor(communityId: string | null): string {
  if (!communityId) return "#5c5854";
  const m = communityId.match(/(\d+)/);
  const idx = m ? (parseInt(m[1], 10) - 1) % CLUSTER_PALETTE.length : 0;
  return CLUSTER_PALETTE[idx];
}

/** Place nodes in cluster sectors, then relax with filtered edges only */
export function layoutGraph(
  graph: SkillGraphData,
  edges: SkillGraphEdge[],
  width: number,
  height: number,
): LayoutNode[] {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const nodes: LayoutNode[] = [];
  const pos = new Map<string, LayoutNode>();

  const entries = Object.entries(graph.neuralCommunities || {}).sort(
    (a, b) => b[1].length - a[1].length,
  );
  const cx = width / 2;
  const cy = height / 2;
  const ring = Math.min(width, height) * 0.38;

  entries.forEach(([, ids], ci) => {
    const angle = (ci / Math.max(1, entries.length)) * Math.PI * 2 - Math.PI / 2;
    const gx = cx + Math.cos(angle) * ring;
    const gy = cy + Math.sin(angle) * ring;
    const localR = 80 + Math.sqrt(ids.length) * 32;
    ids.forEach((id, j) => {
      const base = byId.get(id);
      if (!base) return;
      const a = (j / ids.length) * Math.PI * 2;
      const n: LayoutNode = {
        ...base,
        x: gx + Math.cos(a) * localR,
        y: gy + Math.sin(a) * localR,
        vx: 0,
        vy: 0,
      };
      nodes.push(n);
      pos.set(id, n);
    });
  });

  for (const n of graph.nodes) {
    if (!pos.has(n.id)) {
      const n2: LayoutNode = {
        ...n,
        x: cx + (Math.random() - 0.5) * 120,
        y: cy + (Math.random() - 0.5) * 120,
        vx: 0,
        vy: 0,
      };
      nodes.push(n2);
      pos.set(n.id, n2);
    }
  }

  const linked = edges.filter((e) => pos.has(e.source) && pos.has(e.target));
  const iterations = 100;
  for (let t = 0; t < iterations; t++) {
    const cooling = 1 - t / iterations;
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i];
        const b = nodes[j];
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        const dist = Math.hypot(dx, dy) || 1;
        const force = (12000 / (dist * dist)) * cooling;
        dx = (dx / dist) * force;
        dy = (dy / dist) * force;
        a.vx -= dx;
        a.vy -= dy;
        b.vx += dx;
        b.vy += dy;
      }
    }
    for (const e of linked) {
      const a = pos.get(e.source)!;
      const b = pos.get(e.target)!;
      let dx = b.x - a.x;
      let dy = b.y - a.y;
      const dist = Math.hypot(dx, dy) || 1;
      const ideal = 110 + (1 - e.weight) * 50;
      const pull = (dist - ideal) * 0.035 * cooling;
      dx = (dx / dist) * pull;
      dy = (dy / dist) * pull;
      a.vx += dx;
      a.vy += dy;
      b.vx -= dx;
      b.vy -= dy;
    }
    for (const n of nodes) {
      n.vx += (cx - n.x) * 0.0003 * cooling;
      n.vy += (cy - n.y) * 0.0003 * cooling;
      n.vx *= 0.82;
      n.vy *= 0.82;
      n.x += n.vx;
      n.y += n.vy;
    }
  }

  return nodes;
}

export const DEFAULT_GRAPH_KINDS = new Set([
  "overlap",
  "gap-overlap",
  "router",
  "research",
  "autoresearch",
] as const);

export function nodeRadius(n: { hubScore: number }, scale: number): number {
  const base = 10 + (n.hubScore / 100) * 16;
  return base / Math.max(0.15, scale);
}